
import React, { useEffect, useState } from 'react';
import { User } from '../types';
import { subscriptionService, SubscriptionStatus } from '../services/subscriptionService';
import { authService } from '../services/authService';
import { Sparkles, Loader2, CheckCircle } from './Icons';

interface Props {
  user: User;
  onNavigate: (view: any) => void;
}

const SubscriptionSuccess: React.FC<Props> = ({ user, onNavigate }) => {
  const [sub, setSub] = useState<SubscriptionStatus | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const check = async () => {
      // 1. Force refresh auth token to ensure claims are updated (e.g. is_pro)
      await authService.getCurrentUser();
      
      // 2. Poll DB for record
      const found = await subscriptionService.pollForSubscription();
      setSub(found);
      setLoading(false);
    };
    check();
  }, [user.id]);

  return (
    <div className="min-h-[80vh] flex items-center justify-center p-4 animate-slide-up">
      <div className="glass-panel max-w-lg w-full p-10 rounded-[2.5rem] border border-white/10 relative overflow-hidden text-center">
        
        {/* Background Effects */}
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-64 h-64 bg-gold-500/20 rounded-full blur-[80px] -z-10"></div>
        
        {loading ? (
          <div className="space-y-8 py-10">
            <Loader2 className="w-16 h-16 text-gold-400 animate-spin mx-auto" />
            <div>
               <h2 className="text-2xl font-bold text-white mb-2">Confirming your magic...</h2>
               <p className="text-gray-400">Waiting for payment confirmation.</p>
            </div>
          </div>
        ) : sub ? (
          <div className="space-y-8">
             <div className="w-24 h-24 bg-green-500/20 rounded-full flex items-center justify-center mx-auto shadow-glow ring-1 ring-green-500/50">
                <CheckCircle className="w-12 h-12 text-green-400" />
             </div>
             
             <div className="space-y-2">
                <h1 className="text-3xl font-serif font-bold text-white">You're a Pro!</h1>
                <p className="text-gray-300">Thank you for joining StoryForge Kids.</p>
             </div>

             <div className="bg-white/5 p-6 rounded-2xl border border-white/5 space-y-3">
                <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">Plan</span>
                    <span className="text-white font-bold capitalize">{sub.plan} Subscription</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">Status</span>
                    <span className="text-green-400 font-bold uppercase tracking-wider text-xs bg-green-900/30 px-2 py-1 rounded">Active</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                    <span className="text-gray-400">Next Renewal</span>
                    <span className="text-white font-mono">
                        {new Date(sub.currentPeriodEnd).toLocaleDateString()}
                    </span>
                </div>
             </div>

             <button 
                onClick={() => onNavigate('create')}
                className="w-full py-4 btn-primary-gradient text-white rounded-2xl font-bold shadow-lg shadow-gold-500/20 hover:scale-[1.02] transition-transform flex items-center justify-center gap-2"
             >
                <Sparkles className="w-5 h-5" />
                Start Creating
             </button>
          </div>
        ) : (
           <div className="space-y-6">
             <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto">
                <span className="text-3xl">🤔</span>
             </div>
             <div>
                <h2 className="text-2xl font-bold text-white">Taking a moment...</h2>
                <p className="text-gray-400 mt-2">
                    We haven't received the confirmation yet, but it might just be delayed.
                </p>
             </div>
             <button onClick={() => window.location.reload()} className="text-gold-400 font-bold underline">
                Refresh Page
             </button>
           </div>
        )}
      </div>
    </div>
  );
};

export default SubscriptionSuccess;
